import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Button, Card, CardContent, CardActionArea,
  Grid, Chip, Stack, LinearProgress, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, IconButton, Tooltip, Divider, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import {
  MY_CEREMONIES_QUERY,
  CEREMONY_MATERIALS_QUERY,
  SYNC_CEREMONY_MATERIALS_MUTATION,
  UPDATE_CEREMONY_MATERIAL_MUTATION,
  ADD_CEREMONY_MATERIAL_MUTATION,
  DELETE_CEREMONY_MATERIAL_MUTATION,
} from '../graphql/queries';

const CAT_COLOR = {
  material:   'warning',
  decoration: 'secondary',
  ingredient: 'success',
  equipment:  'info',
};

const CAT_LABEL = {
  material:   'Material',
  decoration: 'Decoration',
  ingredient: 'Ingredient',
  equipment:  'Equipment',
};

const CATEGORIES = ['material', 'decoration', 'ingredient', 'equipment'];

const EMPTY_FORM = { itemName: '', category: 'material', quantity: '', notes: '' };

export default function RitualItemsPage() {
  const [selectedId, setSelectedId]   = useState(null);
  const [catFilter, setCatFilter]     = useState('all');
  const [search, setSearch]           = useState('');
  const [addOpen, setAddOpen]         = useState(false);
  const [addForm, setAddForm]         = useState(EMPTY_FORM);

  // ── Ceremonies ──────────────────────────────────────────────
  const { data: ceremonyData, loading: ceremonyLoading } = useQuery(MY_CEREMONIES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const ceremonies = (ceremonyData?.myCeremonies || []).filter(c => c.status !== 'cancelled');

  // Auto-select first ceremony when list loads
  useEffect(() => {
    if (ceremonies.length > 0 && !selectedId) {
      setSelectedId(ceremonies[0].id);
    }
  }, [ceremonies.length, selectedId]);

  const selectedCeremony = ceremonies.find(c => c.id === selectedId) || null;
  const activeCeremonyId = selectedCeremony?.id || null;

  // ── Materials ────────────────────────────────────────────────
  const { data: matData, loading: matLoading, refetch } = useQuery(CEREMONY_MATERIALS_QUERY, {
    variables: { ceremonyId: activeCeremonyId },
    skip: !activeCeremonyId,
    fetchPolicy: 'cache-and-network',
  });

  const allMaterials   = matData?.ceremonyMaterials || [];
  const neededCount    = allMaterials.filter(m => m.status !== 'not_required').length;
  const pct            = allMaterials.length ? Math.round((neededCount / allMaterials.length) * 100) : 0;

  const catCounts = allMaterials.reduce((acc, m) => {
    acc[m.category] = (acc[m.category] || 0) + 1;
    return acc;
  }, {});

  const filtered = allMaterials.filter(m => {
    const matchCat    = catFilter === 'all' || m.category === catFilter;
    const matchSearch = !search || m.itemName.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  // ── Mutations ────────────────────────────────────────────────
  const [syncMaterials, { loading: syncLoading }] = useMutation(SYNC_CEREMONY_MATERIALS_MUTATION, {
    onCompleted: () => refetch(),
  });

  const [updateMaterial] = useMutation(UPDATE_CEREMONY_MATERIAL_MUTATION, {
    onCompleted: () => refetch(),
  });

  const [addMaterial] = useMutation(ADD_CEREMONY_MATERIAL_MUTATION, {
    onCompleted: () => { setAddOpen(false); setAddForm(EMPTY_FORM); refetch(); },
  });

  const [deleteMaterial] = useMutation(DELETE_CEREMONY_MATERIAL_MUTATION, {
    onCompleted: () => refetch(),
  });

  // ── Handlers ─────────────────────────────────────────────────
  const handleToggle = (mat) => {
    const newStatus = mat.status === 'not_required' ? 'pending' : 'not_required';
    updateMaterial({ variables: { id: mat.id, status: newStatus } });
  };

  const handleSelectAll = () => {
    filtered
      .filter(m => m.status === 'not_required')
      .forEach(m => updateMaterial({ variables: { id: m.id, status: 'pending' } }));
  };

  const handleDeselectAll = () => {
    filtered
      .filter(m => m.status !== 'not_required')
      .forEach(m => updateMaterial({ variables: { id: m.id, status: 'not_required' } }));
  };

  const handleSync = () => syncMaterials({ variables: { ceremonyId: activeCeremonyId } });

  const handleAdd = () => {
    if (!addForm.itemName.trim()) return;
    addMaterial({
      variables: {
        ceremonyId: activeCeremonyId,
        itemName:   addForm.itemName.trim(),
        category:   addForm.category,
        quantity:   addForm.quantity || null,
        notes:      addForm.notes || null,
      },
    });
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────── */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight={700}>Ritual Items Planner</Typography>
        <Typography variant="body2" color="text.secondary">
          Select a ceremony, then tick every item you need — untick anything you don&apos;t.
        </Typography>
      </Box>

      {ceremonyLoading && <LinearProgress sx={{ mb: 2 }} />}

      {!ceremonyLoading && ceremonies.length === 0 && (
        <Alert severity="info">
          No ceremonies planned yet. Go to <strong>Ritual Planning</strong> to add your ceremonies first.
        </Alert>
      )}

      {ceremonies.length > 0 && (
        <>
          {/* ── Ceremony Selector ────────────────────────── */}
          <Card sx={{ mb: 3, px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              SELECT CEREMONY
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {ceremonies.map(c => {
                const isActive = c.id === selectedId;
                const count    = isActive ? allMaterials.length : null;
                const needed   = isActive ? neededCount : null;
                return (
                  <Chip
                    key={c.id}
                    label={count !== null ? `${c.name} (${needed}/${count})` : c.name}
                    onClick={() => { setSelectedId(c.id); setCatFilter('all'); setSearch(''); }}
                    color={isActive ? 'primary' : 'default'}
                    variant={isActive ? 'filled' : 'outlined'}
                    icon={<AutoAwesomeIcon fontSize="small" />}
                    sx={{ fontWeight: isActive ? 700 : 400 }}
                  />
                );
              })}
            </Stack>
          </Card>

          {selectedCeremony && (
            <Grid container spacing={3}>
              {/* ── Left Panel ─────────────────────────────── */}
              <Grid item xs={12} md={3}>
                <Stack spacing={2}>
                  {/* Progress */}
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        {selectedCeremony.name}
                      </Typography>
                      <Chip
                        label={selectedCeremony.ceremonyType?.replace(/_/g, ' ')}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ mb: 2, textTransform: 'capitalize' }}
                      />

                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">Items selected</Typography>
                        <Typography variant="body2" fontWeight={700}>
                          {neededCount} / {allMaterials.length}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ height: 8, borderRadius: 4 }}
                        color={pct === 100 ? 'success' : 'primary'}
                      />

                      <Divider sx={{ my: 2 }} />

                      {/* Category Filter */}
                      <Typography variant="caption" color="text.secondary">FILTER BY CATEGORY</Typography>
                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                        <Chip
                          label={`All  (${allMaterials.length})`}
                          size="small"
                          onClick={() => setCatFilter('all')}
                          color={catFilter === 'all' ? 'primary' : 'default'}
                          variant={catFilter === 'all' ? 'filled' : 'outlined'}
                          sx={{ justifyContent: 'flex-start', width: '100%' }}
                        />
                        {CATEGORIES.map(key =>
                          catCounts[key] ? (
                            <Chip
                              key={key}
                              label={`${CAT_LABEL[key]}  (${catCounts[key]})`}
                              size="small"
                              onClick={() => setCatFilter(key)}
                              color={catFilter === key ? CAT_COLOR[key] : 'default'}
                              variant={catFilter === key ? 'filled' : 'outlined'}
                              sx={{ justifyContent: 'flex-start', width: '100%' }}
                            />
                          ) : null
                        )}
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  <Card>
                    <CardContent>
                      <Stack spacing={1}>
                        {allMaterials.length === 0 ? (
                          <Button
                            variant="contained"
                            startIcon={syncLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                            onClick={handleSync}
                            disabled={syncLoading}
                            fullWidth
                          >
                            {syncLoading ? 'Loading…' : 'Load Default Items'}
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            startIcon={syncLoading ? <CircularProgress size={16} /> : <RestoreIcon />}
                            onClick={handleSync}
                            disabled={syncLoading}
                            fullWidth
                            size="small"
                          >
                            {syncLoading ? 'Adding…' : 'Add Missing Defaults'}
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setAddOpen(true)}
                          fullWidth
                          size="small"
                        >
                          Add Custom Item
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Stack>
              </Grid>

              {/* ── Right Panel — Items ─────────────────────── */}
              <Grid item xs={12} md={9}>
                {/* Search + bulk actions */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    placeholder="Search items…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button size="small" variant="outlined" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                </Stack>

                {matLoading && <LinearProgress sx={{ mb: 2 }} />}

                {!matLoading && allMaterials.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No items yet. Click <strong>&quot;Load Default Items&quot;</strong> to populate recommended items
                    for this ceremony, or add them manually.
                  </Alert>
                )}

                {filtered.length === 0 && allMaterials.length > 0 && (
                  <Alert severity="info">No items match your current filter.</Alert>
                )}

                <Grid container spacing={1.5}>
                  {filtered.map(mat => {
                    const isNeeded = mat.status !== 'not_required';
                    return (
                      <Grid item xs={12} sm={6} lg={4} key={mat.id}>
                        <Card
                          sx={{
                            border: '2px solid',
                            borderColor: isNeeded ? 'success.main' : 'divider',
                            bgcolor: isNeeded ? 'rgba(46,125,50,0.06)' : 'background.paper',
                            transition: 'border-color 0.15s, background-color 0.15s',
                            position: 'relative',
                          }}
                        >
                          <CardActionArea onClick={() => handleToggle(mat)} sx={{ p: 1.5, pr: 5 }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              {isNeeded
                                ? <CheckCircleIcon color="success" sx={{ mt: 0.2, flexShrink: 0 }} />
                                : <RadioButtonUncheckedIcon sx={{ mt: 0.2, flexShrink: 0, color: 'text.disabled' }} />
                              }
                              <Box>
                                <Typography
                                  variant="body2"
                                  fontWeight={isNeeded ? 600 : 400}
                                  color={isNeeded ? 'text.primary' : 'text.secondary'}
                                >
                                  {mat.itemName}
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                                  <Chip
                                    label={CAT_LABEL[mat.category] || mat.category}
                                    size="small"
                                    color={CAT_COLOR[mat.category] || 'default'}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem', px: 0.25 }}
                                  />
                                  {mat.quantity && (
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '18px' }}>
                                      {mat.quantity}
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>
                            </Stack>
                          </CardActionArea>
                          <Tooltip title="Remove item">
                            <IconButton
                              size="small"
                              sx={{ position: 'absolute', top: 6, right: 4 }}
                              onClick={() => deleteMaterial({ variables: { id: mat.id } })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* ── Add Custom Item Dialog ──────────────────────────── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Custom Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Item Name"
              value={addForm.itemName}
              onChange={e => setAddForm(f => ({ ...f, itemName: e.target.value }))}
              fullWidth
              required
              autoFocus
            />
            <TextField
              select
              label="Category"
              value={addForm.category}
              onChange={e => setAddForm(f => ({ ...f, category: e.target.value }))}
              fullWidth
            >
              {CATEGORIES.map(c => (
                <MenuItem key={c} value={c}>{CAT_LABEL[c]}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Quantity (optional)"
              value={addForm.quantity}
              onChange={e => setAddForm(f => ({ ...f, quantity: e.target.value }))}
              fullWidth
              placeholder="e.g. 50 pieces, 1 kg, 500 ml"
            />
            <TextField
              label="Notes (optional)"
              value={addForm.notes}
              onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddOpen(false); setAddForm(EMPTY_FORM); }}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!addForm.itemName.trim()}>
            Add Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
